import type { SeriesPoint, Group } from './dataPipeline'

export interface MultiOverlaySeries {
  /** Timeframe id (tf.id — 'main' | `${label}-${i}`, V4 contract). The STABLE dataKey:
   *  labels may collide (FU-k), ids cannot; 'pos' is unreachable by construction. */
  id: string
  /** Display label (legend/tooltip only). */
  label: string
  /** One timeframe's pivoted, continuous series (from useDynamicsPanels per-timeframe mode). */
  points: SeriesPoint[]
}

export interface MultiOverlayPoint {
  pos: number
  [tfId: string]: number | undefined
}

/** Sum all group values in a point → one total per bucket; all-undefined → gap (undefined), not 0. */
function totalAt(point: SeriesPoint, groups: Group[]): number | undefined {
  let sum: number | undefined = undefined
  for (const grp of groups) {
    const v = point[grp.key]
    if (v === undefined) continue
    sum = (sum ?? 0) + Number(v)
  }
  return sum
}

/** Generalize the 2-way period overlay to N timeframes: collapse each timeframe's per-bucket
 *  series to a single total, then align the totals by positional index (bucket i of each window
 *  lines up). The X position is the index; absolute dates are intentionally discarded (period mode). */
export function buildMultiOverlayPoints(
  series: MultiOverlaySeries[],
  groups: Group[],
): MultiOverlayPoint[] {
  const maxLen = series.reduce((m, s) => Math.max(m, s.points.length), 0)
  const out: MultiOverlayPoint[] = []
  for (let i = 0; i < maxLen; i++) {
    const point: MultiOverlayPoint = { pos: i }
    for (const s of series) {
      const sp = s.points[i]
      point[s.id] = sp ? totalAt(sp, groups) : undefined
    }
    out.push(point)
  }
  return out
}
